/**
 * Vault-equivalent values for `atlasCore.color` subtrees that affect branding.
 *
 * Mapping derived from `tokenMapping.ts` (which reverses Vault → Atlas).
 * Only `gray`, `red`, and `brand` subtrees are overridden — they are the only
 * core-color subtrees referenced directly by component code (verified via grep).
 *
 * `green`, `yellow`, `blue`, `purple`, `indigo`, `flamingo` core scales are
 * referenced only by `dataVisualization.*` semantic palettes (already overridden).
 *
 * Spacing, radius, shadow are structural — left as Atlas pass-through.
 */

import {
  vaultGreen,
  vaultNeutral,
  vaultRed,
  vaultTurquoise,
  vaultYellow,
} from './vaultPaletteSource';

/** Vault gray scale — maps Atlas gray rungs to Vault neutrals from tokenMapping.ts */
export const vaultCoreGray = {
  0: vaultNeutral.N0, // #FFFFFF
  50: vaultNeutral.N100, // #FAFBFF — was #fafafa
  100: vaultNeutral.N200, // #F2F2F3 — was #f3f3f3
  200: vaultNeutral.N300, // #E4E5E6 — was #e6e6e6
  300: vaultNeutral.N300, // #E4E5E6 — was #dadada (Vault has no exact equiv)
  400: vaultNeutral.N400, // #C6C8C9 — was #b6b8b9
  500: vaultNeutral.N500, // #A2A6A8 — was #a0a2a5
  600: vaultNeutral.N600, // #71717A — was #8c8e92
  700: vaultNeutral.N600, // #71717A — was #6f7377
  800: vaultNeutral.N700, // #546276 — was #464e53
  900: vaultNeutral.N800, // #17353D — was #282e37
  950: vaultNeutral.N900, // #12292F — was #121a26 (dark chrome — was N900 per migration doc)
  1000: vaultNeutral.N1000, // #0A181B — was #000000
};

/** Vault red scale — Atlas red rungs to Vault reds */
export const vaultCoreRed = {
  10: vaultRed.R500, // darkest
  20: vaultRed.R500,
  25: vaultRed.R500,
  30: vaultRed.R500,
  35: vaultRed.R400,
  40: vaultRed.R400,
  50: vaultRed.R300,
  60: vaultRed.R200, // #FF5F5F — used by Spinner
  70: vaultRed.R200,
  80: vaultRed.R200,
  90: vaultRed.R100,
  95: vaultRed.R100,
  98: vaultRed.R100,
};

/** Vault green scale (used only by dataVisualization indirectly; provide for safety) */
export const vaultCoreGreen = {
  10: vaultGreen.G500,
  20: vaultGreen.G500,
  25: vaultGreen.G500,
  30: vaultGreen.G400,
  35: vaultGreen.G400,
  40: vaultGreen.G400,
  50: vaultGreen.G300,
  60: vaultGreen.G300,
  70: vaultGreen.G200,
  80: vaultGreen.G200,
  90: vaultGreen.G200,
  95: vaultGreen.G100,
  98: vaultGreen.G100,
};

/** Vault yellow scale (parity with Atlas rungs, mapped to Vault yellows) */
export const vaultCoreYellow = {
  10: vaultYellow.Y500,
  20: vaultYellow.Y500,
  25: vaultYellow.Y500,
  30: vaultYellow.Y500,
  35: vaultYellow.Y400,
  40: vaultYellow.Y400,
  50: vaultYellow.Y400,
  60: vaultYellow.Y300,
  70: vaultYellow.Y300,
  80: vaultYellow.Y200,
  90: vaultYellow.Y200,
  95: vaultYellow.Y100,
  98: vaultYellow.Y100,
};

/**
 * Brand colors — Vault never had a Diligent brand.
 * Map brand red → Vault turquoise primary; brand grays → Vault neutrals.
 */
export const vaultCoreBrand = {
  diligentRed: vaultTurquoise.T300, // #35A191 — replaces brand red with Vault primary
  red2: vaultRed.R400,
  red3: vaultRed.R400,
  red4: vaultRed.R500,
  red5: vaultRed.R500,
  blue1: '#1dd9f9', // Vault palette.info.light
  blue2: '#1dd9f9',
  blue3: '#0b4cce',
  purple1: vaultTurquoise.T200, // no Vault purple → soft turquoise
  purple2: vaultTurquoise.T300,
  purple3: vaultTurquoise.T400,
  gray1: vaultNeutral.N200,
  gray2: vaultNeutral.N300,
  gray3: vaultNeutral.N500,
  gray4: vaultNeutral.N600,
  gray5: vaultNeutral.N800,
};
